import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { Badge, BottomSheetContentLayout, Menu, Popover, useDevice, useOverlayContext } from 'seagull';
import { Checkmark } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLegalTerms } from '~/hooks/reactQuery/queries/useLegalTerms';
import { useLocaleKeys } from '~/i18n';
import { TriageCollapseWrapper } from '../TriageCollapseWrapper/TriageCollapseWrapper';
import { TriageFieldBox } from '../TriageFieldBox/TriageFieldBox';
import { TriageRow } from '../TriageRow/TriageRow';
import { EditExtractedLegalTermsPanel } from './EditExtractedLegalTermsPanel/EditExtractedLegalTermsPanel';
import s from './TriageTermsOfServiceRow.module.scss';
export const GetTriageTermsOfServiceRowTID = (parentTestId)=>getTestIds(parentTestId);
export const TermsOfServiceRowTestIds = GetTriageTermsOfServiceRowTID().TermsOfServiceRowTestIds;
const getLegalTermsItemSubtitle = (item, textDocumentSubtitle)=>{
    if (item.contentType === 'pdf') {
        return item.pdfFileName === item.name ? undefined : item.pdfFileName;
    }
    return textDocumentSubtitle;
};
const renderSelectedCheckmark = (testId)=>/*#__PURE__*/ _jsx(Checkmark, {
        size: 20,
        testId: "".concat(testId, "-checkmark")
    });
const buildPickerItems = (param)=>{
    let { availableLegalTerms, selectedId, snapshot, isUnmatched, textDocumentSubtitle, onSelectExisting, onSelectExtracted } = param;
    const existingItems = availableLegalTerms.map((item)=>{
        const testId = TermsOfServiceRowTestIds.Picker.Option(item.id);
        const selected = item.id === selectedId;
        return {
            id: item.id,
            testId,
            title: item.name,
            body: getLegalTermsItemSubtitle(item, textDocumentSubtitle),
            selected,
            suffixIcon: selected ? renderSelectedCheckmark(testId) : undefined,
            bi: {
                name: 'triage_terms_of_service_picker_select_existing',
                data: {
                    legal_terms_id: item.id
                }
            },
            onClick: ()=>onSelectExisting(item.id)
        };
    });
    if (snapshot == null) {
        return existingItems;
    }
    const extractedTestId = TermsOfServiceRowTestIds.Picker.ExtractedOption();
    return [
        ...existingItems,
        {
            id: '__extracted__',
            testId: extractedTestId,
            title: snapshot.name,
            body: textDocumentSubtitle,
            selected: isUnmatched,
            suffixIcon: isUnmatched ? renderSelectedCheckmark(extractedTestId) : undefined,
            bi: {
                name: 'triage_terms_of_service_picker_select_extracted'
            },
            onClick: onSelectExtracted
        }
    ];
};
const LegalTermsPickerSheet = (param)=>{
    let { items } = param;
    const { t } = useLocaleKeys('agreementImport.triageModal');
    const { hideBottomSheet } = useOverlayContext();
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        header: {
            title: t.termsOfServiceChangeDocumentButton(),
            titleTestId: TermsOfServiceRowTestIds.MobilePickerSheetTitle(),
            gutter: 'm'
        },
        children: /*#__PURE__*/ _jsx(Menu, {
            testId: TermsOfServiceRowTestIds.Picker.Root(),
            items: items.map((item)=>_object_spread_props(_object_spread({}, item), {
                    onClick: ()=>{
                        item.onClick();
                        hideBottomSheet();
                    }
                }))
        })
    });
};
const LegalTermsEditSheet = (param)=>{
    let { model, onModelChange, onChangeRequest } = param;
    const { t } = useLocaleKeys();
    const { hideBottomSheet } = useOverlayContext();
    const [liveModel, setLiveModel] = useState(model);
    const liveModelRef = useRef(liveModel);
    liveModelRef.current = liveModel;
    const handleSave = ()=>{
        if (liveModelRef.current !== model) {
            onModelChange(liveModelRef.current);
        }
        hideBottomSheet();
    };
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentClassName: s.sheetContent,
        footer: {
            primaryAction: {
                bi: {
                    name: 'save_triage_terms_of_service'
                },
                text: t.common.buttons.save(),
                testId: TermsOfServiceRowTestIds.MobileSaveButton(),
                onClick: handleSave
            },
            secondaryAction: {
                text: t.agreementImport.triageModal.termsOfServiceChangeDocumentButton(),
                testId: TermsOfServiceRowTestIds.MobileChangeDocumentButton(),
                onClick: onChangeRequest
            }
        },
        children: /*#__PURE__*/ _jsx(EditExtractedLegalTermsPanel, {
            model: liveModel,
            onModelChange: setLiveModel,
            testId: TermsOfServiceRowTestIds.EditPanel()
        })
    });
};
export const TriageTermsOfServiceRow = (param)=>{
    let { model, onModelChange, wrapperTestId } = param;
    const { t } = useLocaleKeys('agreementImport.triageModal');
    const { isMobileDevice } = useDevice();
    const { pushBottomSheetPage } = useOverlayContext();
    const [panelOpen, setPanelOpen] = useState(false);
    const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
    const pickerOpen = pickerAnchorEl != null;
    const { legalTerms } = useLegalTerms();
    const currentLegalTerms = model.legalTerms();
    const isUnmatched = model.isLegalTermsUnmatched();
    const selectedId = model.legalTermsSelectedId();
    const snapshot = model.extractedDocSnapshot();
    const displayName = useMemo(()=>{
        if (selectedId != null) {
            var _ref;
            var _legalTerms_find;
            return (_ref = (_legalTerms_find = legalTerms.find((lt)=>lt.id === selectedId)) === null || _legalTerms_find === void 0 ? void 0 : _legalTerms_find.name) !== null && _ref !== void 0 ? _ref : '';
        }
        if ((currentLegalTerms === null || currentLegalTerms === void 0 ? void 0 : currentLegalTerms.matchStatus) === 'unmatched') {
            return currentLegalTerms.name;
        }
        return '';
    }, [
        selectedId,
        legalTerms,
        currentLegalTerms
    ]);
    if (currentLegalTerms == null) {
        return null;
    }
    const commitSelectExisting = (id)=>{
        onModelChange(model.matchLegalTermsToExisting(id));
    };
    const commitSelectExtracted = ()=>{
        onModelChange(model.revertToExtractedSnapshot());
    };
    const pickerItems = buildPickerItems({
        availableLegalTerms: legalTerms,
        selectedId,
        snapshot,
        isUnmatched,
        textDocumentSubtitle: t.termsOfServiceTextDocumentSubtitle(),
        onSelectExisting: commitSelectExisting,
        onSelectExtracted: commitSelectExtracted
    });
    const openSheet = ()=>{
        if (snapshot != null) {
            pushBottomSheetPage({
                content: /*#__PURE__*/ _jsx(LegalTermsEditSheet, {
                    model: model,
                    onModelChange: onModelChange,
                    onChangeRequest: ()=>pushBottomSheetPage({
                            content: /*#__PURE__*/ _jsx(LegalTermsPickerSheet, {
                                items: pickerItems
                            })
                        })
                })
            });
        } else {
            pushBottomSheetPage({
                content: /*#__PURE__*/ _jsx(LegalTermsPickerSheet, {
                    items: pickerItems
                })
            });
        }
    };
    const mobileBody = /*#__PURE__*/ _jsxs("div", {
        className: s.mobileSummaryLine,
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: s.mobileSummaryText,
                children: displayName
            }),
            isUnmatched && /*#__PURE__*/ _jsx("span", {
                "data-testid": TermsOfServiceRowTestIds.NewDocumentBadge(),
                className: s.mobileSummaryBadge,
                children: /*#__PURE__*/ _jsx(Badge, {
                    skin: "primary",
                    rounded: true,
                    outlined: true,
                    children: t.termsOfServiceNewDocumentBadge()
                })
            })
        ]
    });
    const actions = [
        ...isUnmatched ? [
            {
                text: t.termsOfServiceEditButton(),
                testId: TermsOfServiceRowTestIds.EditButton(),
                onClick: ()=>setPanelOpen((o)=>!o)
            }
        ] : [],
        {
            text: t.termsOfServiceChangeButton(),
            testId: TermsOfServiceRowTestIds.ChangeButton(),
            onClick: (event)=>setPickerAnchorEl(event.currentTarget)
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": TermsOfServiceRowTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(TriageRow, {
                label: t.termsOfServiceLabel(),
                wrapperTestId: wrapperTestId,
                mobile: {
                    body: mobileBody,
                    bodyTestId: TermsOfServiceRowTestIds.MobileSummaryBody(),
                    onEdit: openSheet,
                    editTestId: TermsOfServiceRowTestIds.MobileEditButton()
                },
                children: /*#__PURE__*/ _jsx(TriageFieldBox, {
                    variant: {
                        type: 'text',
                        value: displayName,
                        testId: TermsOfServiceRowTestIds.Summary()
                    },
                    forceActionsVisible: pickerOpen || panelOpen,
                    badges: isUnmatched && /*#__PURE__*/ _jsx("span", {
                        "data-testid": TermsOfServiceRowTestIds.NewDocumentBadge(),
                        children: /*#__PURE__*/ _jsx(Badge, {
                            skin: "primary",
                            rounded: true,
                            outlined: true,
                            children: t.termsOfServiceNewDocumentBadge()
                        })
                    }),
                    actions: actions
                })
            }),
            !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Popover, {
                        anchorEl: pickerAnchorEl,
                        open: pickerOpen,
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right'
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right'
                        },
                        onClose: ()=>setPickerAnchorEl(null),
                        children: /*#__PURE__*/ _jsx(Menu, {
                            testId: TermsOfServiceRowTestIds.Picker.Root(),
                            items: buildPickerItems({
                                availableLegalTerms: legalTerms,
                                selectedId,
                                snapshot,
                                isUnmatched,
                                textDocumentSubtitle: t.termsOfServiceTextDocumentSubtitle(),
                                onSelectExisting: (id)=>{
                                    commitSelectExisting(id);
                                    setPickerAnchorEl(null);
                                    setPanelOpen(false);
                                },
                                onSelectExtracted: ()=>{
                                    commitSelectExtracted();
                                    setPickerAnchorEl(null);
                                }
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(TriageCollapseWrapper, {
                        open: panelOpen,
                        testId: TermsOfServiceRowTestIds.Panel(),
                        children: /*#__PURE__*/ _jsx(EditExtractedLegalTermsPanel, {
                            model: model,
                            onModelChange: onModelChange,
                            testId: TermsOfServiceRowTestIds.EditPanel()
                        })
                    })
                ]
            })
        ]
    });
};
